import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonToolbar, Content, Icon, Loader, Nav } from 'rsuite';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { get, size } from 'lodash/fp';
import qs from 'qs';
import styled from 'styled-components';
import {
  MERGE as GUEST_MERGE,
  MODULE as GUEST_MODULE,
  AUDIT as GUEST_AUDIT
} from '../../../constants/permissions/guest';
import {
  fullAccess,
  hasViewAccess,
  viewAccess
} from '../../../helpers/permission';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import fullNameFormat from '../../../helpers/formatFullName';
import { fetchSettings } from '../../../redux/Settings/sagas/main/routines';
import {
  fetchGuestForView,
  deleteGuest
} from '../../../redux/Guest/sagas/routines';
import { fetchClientColumns } from '../../../redux/sagas/client/routines';
import { fetchHouseholdMemberColumns } from '../../../redux/sagas/routines';
import { clearGuestView } from '../../../redux/Guest/actions/routes/view';
import { clearGuestFiles } from '../../../redux/Guest/actions/file';
import { clearGuestNotes } from '../../../redux/Guest/actions/note';
import { clearGuestAuditLogs } from '../../../redux/Guest/actions/audit-log';
import {
  getMainSettingsData,
  getIsHouseholdDetails
} from '../../../redux/Settings/selectors/main';
import {
  getGuestViewData,
  getIsDeletingGuest,
  getIsFetched
} from '../../../redux/Guest/selectors/routes/view';
import PageHeader from '../../../components/PageHeader';
import PageLayout from '../../../components/guest/PageLayout';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import CustomBreadcrumb from '../../../components/Breadcrumb';
import PrimarySection from './PrimarySection';
import HouseholdSection from './HouseholdSection';
import MembersSection from './MembersSection';
import VisitsSection from './VisitsSection';
import NotesSection from './NotesSection';
import FilesSection from './FilesSection';
import AuditSection from './AuditSection';
import MainDetails from './MainDetails';
import ReservationSection from './ReservationSection';
import history from '../../history';
import { guestsBasePath, guestsMergePath, guestsViewPath } from '../../Paths';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import { MANAGE_FILE, MANAGE_NOTE } from '../../../constants/permissions';

const NavItem = styled(Nav.Item)`
  > a.rs-nav-item-content {
    font-weight: strong;
    font-size: 15px !important;
  }
`;

export default () => {
  const dispatch = useDispatch();
  const { id, section } = useParams();
  const { search } = useLocation();
  const query = qs.parse(search, { ignoreQueryPrefix: true });
  const shouldOpenPortalUpdates = query.fromPortal === '1';
  const settings = useSelector(getMainSettingsData);
  const isHouseholdDetails = useSelector(getIsHouseholdDetails);
  const guest = useSelector(getGuestViewData);
  const isFetched = useSelector(getIsFetched);
  const isDeleting = useSelector(getIsDeletingGuest);
  const firstName = get('firstname', guest);
  const middleName = get('middlename', guest);
  const lastName = get('lastname', guest);
  const householdMembersCount = get('household_total', guest) - 1;
  const actualMembersCount = size(get('othersHousehold', guest)) || 0;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const permissions = useSelector(getUserPermissionsAsMap);

  useEffect(
    () => {
      dispatch(fetchGuestForView(id));
      dispatch(fetchSettings());
      dispatch(fetchClientColumns());
      dispatch(fetchHouseholdMemberColumns());
      return () => {
        dispatch(clearGuestView());
        dispatch(clearGuestFiles());
        dispatch(clearGuestNotes());
        dispatch(clearGuestAuditLogs());
      };
    },
    [id]
  );

  const handleSelect = key => {
    history.push(
      `${pagePathToUrl(guestsViewPath, { id, section: key })}${search}`
    );
  };

  const handleMerge = () => {
    history.push(pagePathToUrl(guestsMergePath, { id }));
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDeleteItem = () => {
    setShowDeleteModal(false);
    dispatch(deleteGuest(id));
  };

  if (isFetched && !guest) {
    return <Redirect to={guestsBasePath} />;
  }

  if (shouldOpenPortalUpdates && section !== 'household') {
    return (
      <Redirect
        to={`${pagePathToUrl(guestsViewPath, {
          id,
          section: 'household'
        })}${search}`}
      />
    );
  }

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(GUEST_MODULE)}
        fallback={<AccessDenied />}
      >
        <CustomBreadcrumb
          items={[
            {
              path: guestsBasePath,
              label: 'Guests'
            },
            { label: fullNameFormat([firstName, middleName, lastName]) }
          ]}
        />

        <PageHeader title="" />
        <Content>
          {guest ? (
            <Fragment>
              <ButtonToolbar style={{ float: 'right' }}>
                <AccessControl allowOnlyFor={fullAccess(GUEST_MODULE)}>
                  <Button color="red" onClick={handleShowDeleteModal}>
                    <Icon icon="trash" /> Delete
                  </Button>
                </AccessControl>
                <AccessControl allowOnlyFor={fullAccess(GUEST_MERGE)}>
                  <Button onClick={handleMerge} appearance="primary">
                    <Icon icon="compress" /> Merge
                  </Button>
                </AccessControl>
              </ButtonToolbar>

              <MainDetails guest={guest} settings={settings} />
              <Nav
                activeKey={section}
                onSelect={handleSelect}
                appearance="subtle"
              >
                <NavItem eventKey="primary">Primary</NavItem>
                <NavItem eventKey="household">Household</NavItem>
                {isHouseholdDetails && (
                  <NavItem eventKey="members">
                    Other Members(
                    {Math.max(householdMembersCount, actualMembersCount)})
                  </NavItem>
                )}
                {hasViewAccess(GUEST_MODULE, permissions) && (
                  <NavItem eventKey="visits">Visits</NavItem>
                )}
                {hasViewAccess(MANAGE_NOTE, permissions) && (
                  <NavItem eventKey="notes">Notes</NavItem>
                )}
                {hasViewAccess(GUEST_AUDIT, permissions) && (
                  <NavItem eventKey="audit">Audit Trail</NavItem>
                )}
                {hasViewAccess(MANAGE_FILE, permissions) && (
                  <NavItem eventKey="files">Files</NavItem>
                )}
                {hasViewAccess(PERMISSIONS.MODULE, permissions) && (
                  <NavItem eventKey="reservations">Reservations</NavItem>
                )}
              </Nav>
              {section === 'primary' && (
                <PrimarySection guest={guest} settings={settings} />
              )}
              {section === 'household' && (
                <HouseholdSection guest={guest} settings={settings} />
              )}
              {section === 'members' && <MembersSection guest={guest} />}
              {section === 'visits' && <VisitsSection guestId={guest.id} />}
              {section === 'notes' && <NotesSection guestId={guest.id} />}
              {section === 'audit' && <AuditSection guestId={guest.id} />}
              {section === 'files' && <FilesSection guestId={guest.id} />}
              {section === 'reservations' && (
                <ReservationSection guestId={guest.id} />
              )}
            </Fragment>
          ) : (
            <Loader content="Loading..." center />
          )}

          <DeleteConfirmationModal
            message="WARNING!!! You are about to DELETE the ENTIRE Guest record. By clicking Delete you will erase everything about this Guest including ALL VISIT HISTORY. It will be as if they never existed. Are you sure you no longer want this Guest in your system? Consider running a report first to retain data before deleting. By clicking delete you are agreeing to a $65/hr fee to retrieve this data, should you change your mind. For help call 1-844-896-9577."
            show={showDeleteModal}
            onHide={handleHideDeleteModal}
            onConfirm={handleConfirmDeleteItem}
            isDeleting={isDeleting}
          />
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
